import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { OkResponse, SecurityActivityResponse } from "@starsach/shared-types";
import { actionLabel, resultTone } from "../../lib/audit-labels";
import { useApi } from "../../api/hooks";
import { ApiRequestError } from "../../api/client";
import { rememberPendingSignOut } from "../../auth/pending-sign-out";
import { useAuth } from "../../auth/AuthProvider";
import { otpauthUri, portalHost } from "../../auth/otpauth";
import { CognitoApiError, cognitoApi, credentialToJson, passkeysPossible, toWebAuthnCreationOptions, type DeviceRecord, type PasskeyRecord } from "../../auth/cognito-api";
import { securityError } from "../../auth/security-words";
import { readTokens, removeRememberedDevice, unrememberSessionDevice } from "../../auth/session-store";
import { StepUpSessionEnded, useStepUp } from "../../auth/StepUpProvider";
import { useInvalidatePasskeys } from "../../auth/usePasskeys";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { useFormat } from "../../lib/useFormat";

/**
 * Requirements 13.3. Everything here is the browser talking to Cognito with
 * the user's own access token - no route on our API can change how someone
 * signs in. The one exception is the activity view, which the API assembles
 * from Cognito's history and the user's own audit rows.
 */
export function SecuritySection() {
  const { session, config, me, accessToken } = useAuth();
  const api = useMemo(() => (config ? cognitoApi(config) : undefined), [config]);
  // The token at the moment of each call (gairm/lms#430): a step-up or a renewal since the render has replaced it.
  const token = useCallback<Token>(
    async (renew) => {
      const t = await accessToken(renew);
      if (!t) throw new StepUpSessionEnded();
      return t;
    },
    [accessToken],
  );
  if (!api || !session?.accessToken) return null;
  if (me?.viewAs) {
    return (
      <Card title="Security">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">Sign-in settings are your own and are managed from your own tab, not from inside a customer's portal.</p>
      </Card>
    );
  }
  if (me?.identityProvider) {
    // ADR-0014: password, MFA, passkeys and remembered devices are the provider's; Cognito holds none of them for this user.
    return (
      <div id="security" className="scroll-mt-6 space-y-6">
        <Card title="Sign-in">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            You sign in through <span className="font-medium text-ink dark:text-white">{me.identityProvider}</span>, your organization's identity provider. Your password, multi-factor authentication, passkeys and trusted devices are managed there, not here.
          </p>
          {/* R7, the wording Chris approved on 1 Oct. */}
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Sign out everywhere ends your sessions in Starsach. Your next sign-in may not ask for your password while you are still signed in at {me.identityProvider}; that session is your organization's to end.
          </p>
          <SignOutEverywhere className="mt-4" />
        </Card>
        <SecurityActivity />
      </div>
    );
  }
  return (
    <div id="security" className="scroll-mt-6 space-y-6">
      <Passkeys api={api} token={token} />
      <Authenticator api={api} token={token} />
      <Devices api={api} token={token} />
      <Password api={api} token={token} />
      <SecurityActivity />
    </div>
  );
}

type Api = ReturnType<typeof cognitoApi>;
/** The access token now; `renew` forces a renewal first. */
type Token = (renew?: boolean) => Promise<string>;

/** One Cognito call with the token as it is now, renewed and tried once more if Cognito says it has expired (gairm/lms#430). */
async function call<T>(token: Token, fn: (accessToken: string) => Promise<T>): Promise<T> {
  try {
    return await fn(await token());
  } catch (e) {
    if (e instanceof CognitoApiError && e.code === "NotAuthorizedException" && /expired/i.test(e.message)) return fn(await token(true));
    throw e;
  }
}

/** WebAuthn: a credential answers only on its relying party or a subdomain of it. */
export function usableHere(c: Pick<PasskeyRecord, "RelyingPartyId">, hostname = typeof window === "undefined" ? "" : window.location.hostname): boolean {
  const rp = c.RelyingPartyId?.toLowerCase();
  if (!rp) return true;
  const host = hostname.toLowerCase();
  return host === rp || host.endsWith(`.${rp}`);
}

function useCognitoList<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      setData(await load());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [load]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload };
}

/** 13.3 [20]. The public key and credential id live in Cognito; the private key never leaves the authenticator. */
function Passkeys({ api, token }: { api: Api; token: Token }) {
  const { ensureFresh } = useStepUp();
  const list = useCognitoList(useCallback(() => call(token, (t) => api.listPasskeys(t)).then((r) => r.Credentials ?? []), [api, token]));
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const id = useId();
  const supported = passkeysPossible();
  const invalidatePasskeys = useInvalidatePasskeys();

  const enrol = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      const start = await call(token, (t) => api.startPasskey(t));
      const created = await navigator.credentials.create(toWebAuthnCreationOptions(start.CredentialCreationOptions));
      await call(token, (t) => api.completePasskey(t, credentialToJson(created)));
      setLabel("");
      await list.reload();
      void invalidatePasskeys();
    } catch (e) {
      setError(securityError(e, "passkey-add"));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (c: PasskeyRecord) => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await call(token, (t) => api.deletePasskey(t, c.CredentialId));
      await list.reload();
      void invalidatePasskeys();
    } catch (e) {
      setError(securityError(e, "passkey-remove"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Passkeys">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        A passkey signs you in with your fingerprint, face or security key instead of a password and code, and answers the "confirm it's you" step with a touch. Enroll one per device you sign in from.
      </p>
      {!supported && <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">This browser does not support passkeys.</p>}
      {list.loading && <LoadingState label="Loading passkeys" />}
      {list.error !== undefined && <ErrorState error={list.error} onRetry={() => void list.reload()} />}
      {list.data && (
        <div className="mt-3">
          <DataTable
            caption="Your passkeys"
            rows={list.data}
            rowKey={(c) => c.CredentialId}
            filter={false}
            empty={<EmptyState title="No passkeys yet" hint="Enroll one below; you will keep your authenticator app as well." />}
            columns={[
              { key: "name", header: "Name", render: (c) => c.FriendlyCredentialName ?? "Passkey" },
              { key: "kind", header: "Kind", render: (c) => (c.AuthenticatorAttachment === "platform" ? "This device" : c.AuthenticatorAttachment === "cross-platform" ? "Security key" : "—") },
              // WebAuthn's own rule: a credential answers only on its relying party or a subdomain of it. Cognito keeps the
              // record when the relying-party id changes (it happened on 2026-09-14), so the row must say it is dead.
              { key: "usable", header: "Usable here", render: (c) => (usableHere(c) ? <StatusPill status="yes" tone="ok" /> : <span title={`Enrolled for ${c.RelyingPartyId ?? "another site"}; remove it and enroll again.`}><StatusPill status="no" tone="warning" /></span>) },
              { key: "since", header: "Enrolled", render: (c) => (c.CreatedAt ? <RelativeTime iso={new Date(c.CreatedAt * 1000).toISOString()} /> : "—") },
              { key: "act", header: "", render: (c) => <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => void remove(c)}>Remove</button> },
            ]}
          />
        </div>
      )}
      <form
        className="mt-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void enrol();
        }}
      >
        <label htmlFor={id} className="text-sm">
          <span className="block font-medium">Name for this passkey</span>
          <input id={id} className={`${inputClass} w-64`} value={label} placeholder="MacBook Touch ID" onChange={(e) => setLabel(e.target.value)} />
        </label>
        <button type="submit" className={btnPrimary} disabled={!supported || busy}>
          {busy ? "Working…" : "Enroll a passkey"}
        </button>
      </form>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/** 13.3 [15]. A new authenticator replaces the old one once its first code is verified; the account is never without one. */
function Authenticator({ api, token }: { api: Api; token: Token }) {
  const { session, me } = useAuth();
  const { ensureFresh } = useStepUp();
  const [secret, setSecret] = useState<string>();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>();
  const id = useId();
  const email = session?.email ?? me?.email ?? "";
  const host = portalHost();
  const otpauth = secret && email.trim() ? otpauthUri(host, email, secret) : undefined;

  const begin = async () => {
    setBusy(true);
    setError(undefined);
    setDone(false);
    try {
      await ensureFresh();
      const r = await call(token, (t) => api.associateSoftwareToken(t));
      if (!r.SecretCode) throw new Error("Cognito returned no secret");
      setSecret(r.SecretCode);
    } catch (e) {
      setError(securityError(e, "authenticator"));
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await call(token, (t) => api.verifySoftwareToken(t, code, "Authenticator app"));
      if (r.Status !== "SUCCESS") throw new CognitoApiError("CodeMismatchException", "The code did not match");
      // Managed login's own MFA setup leaves the per-user preference unset (the pool requires MFA anyway); enrolling here records it.
      await call(token, (t) => api.setTotpPreference(t));
      setSecret(undefined);
      setCode("");
      setDone(true);
    } catch (e) {
      setError(securityError(e, "authenticator"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Authenticator app">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        Required for every password sign-in, so yours is set up. Cognito keeps one authenticator per account and does not record when it was set up. Moving to a new phone means enrolling it here; the old one stops working once the new one's first code is verified.
      </p>
      {!secret ? (
        <button type="button" className={`${btnSecondary} mt-3`} disabled={busy} onClick={() => void begin()}>
          Enroll a new authenticator
        </button>
      ) : (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p className="text-sm">Add this to your authenticator app, then enter the code it shows.</p>
          <p className="break-all rounded bg-neutral-100 p-2 font-mono text-xs dark:bg-neutral-800" aria-label="Authenticator secret">{secret}</p>
          <p className="text-xs text-neutral-600 dark:text-neutral-400" data-testid="setup-key-label">If the app asks for a name, use <strong className="break-all">{host}</strong>{email.trim() ? <>, and <strong className="break-all">{email.trim()}</strong> for the account</> : null}.</p>
          {otpauth && <a href={otpauth} className="text-sm text-accent-strong underline dark:text-accent-bright">Open in an authenticator app on this device</a>}
          <label htmlFor={id} className="block text-sm">
            <span className="block font-medium">Code from the app</span>
            <input id={id} inputMode="numeric" autoComplete="one-time-code" className={`${inputClass} w-40`} value={code} pattern="[0-9]{6}" required onChange={(e) => setCode(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className={btnPrimary} disabled={busy}>Verify and switch</button>
            <button type="button" className={btnSecondary} onClick={() => setSecret(undefined)}>Cancel</button>
          </div>
        </form>
      )}
      {done && <p role="status" className="mt-2 text-sm text-green-700 dark:text-green-300">Your new authenticator is active.</p>}
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/** 13.3 [14] and [16]. Cognito enforces the policy and history; every other session is signed out afterwards. */
function Password({ api, token }: { api: Api; token: Token }) {
  const { ensureFresh } = useStepUp();
  const everywhere = useSignOutEverywhere();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const ids = { current: useId(), next: useId(), again: useId() };
  const mismatch = again.length > 0 && next !== again;

  const change = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      await call(token, (t) => api.changePassword(t, current, next));
    } catch (e) {
      setError(securityError(e, "password"));
      setBusy(false);
      return;
    }
    // Every session is out, this one too, and it signs in again with the new password (R4: stamp, devices, global sign-out).
    await everywhere.run({ afterPasswordChange: true }).catch((e: unknown) => {
      setError(securityError(e, "sign-out"));
      setBusy(false);
    });
  };

  return (
    <Card title="Password and sessions">
      <form
        className="grid gap-3 md:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) void change();
        }}
      >
        <label htmlFor={ids.current} className="text-sm">
          <span className="block font-medium">Current password</span>
          <input id={ids.current} type="password" autoComplete="current-password" className={inputClass} value={current} required onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label htmlFor={ids.next} className="text-sm">
          <span className="block font-medium">New password</span>
          <input id={ids.next} type="password" autoComplete="new-password" className={inputClass} value={next} required minLength={12} onChange={(e) => setNext(e.target.value)} />
          <span className="mt-1 block text-xs text-neutral-500">At least 12 characters, with upper and lower case, a number and a symbol.</span>
        </label>
        <label htmlFor={ids.again} className="text-sm">
          <span className="block font-medium">New password again</span>
          <input id={ids.again} type="password" autoComplete="new-password" className={inputClass} value={again} required aria-invalid={mismatch || undefined} aria-describedby={mismatch ? `${ids.again}-err` : undefined} onChange={(e) => setAgain(e.target.value)} />
          {mismatch && <span id={`${ids.again}-err`} role="alert" className="mt-1 block text-xs text-red-700 dark:text-red-300">The two do not match.</span>}
        </label>
        <div className="md:col-span-3 flex flex-wrap items-center gap-3">
          <button type="submit" className={btnPrimary} disabled={busy || mismatch || !current || !next}>
            {busy ? "Working…" : "Change password"}
          </button>
          <span className="text-xs text-neutral-500">Changing it signs every session out, including this one; you sign in again with the new password.</span>
        </div>
      </form>
      <SignOutEverywhere className="mt-5 border-t border-neutral-200 pt-4 dark:border-neutral-800" disabled={busy} />
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

/**
 * R4, R11: Sign out everywhere runs on the server (POST /v1/me/security/sign-out-everywhere): the stamp first, so every
 * token issued until now is refused at once; then every remembered device forgotten, so none skips the next MFA; then
 * Cognito's global sign-out. Then this browser signs out, with the stamp already written.
 */
function useSignOutEverywhere() {
  const { signOut, config, me, accessToken } = useAuth();
  const { ensureFresh } = useStepUp();
  const post = useApi();
  return {
    run: async (opts: { afterPasswordChange?: boolean } = {}) => {
      if (!opts.afterPasswordChange) await ensureFresh();
      const at = new Date().toISOString();
      try {
        await post<OkResponse>("/v1/me/security/sign-out-everywhere", { method: "POST", signal: AbortSignal.timeout(30_000) });
      } catch (e) {
        // A refusal is said. A server that could not be reached (or failed) still signs out everywhere (gairm/lms#439):
        // this browser forgets its own device and calls Cognito's GlobalSignOut with its own token, so the other
        // sessions' refresh tokens stop renewing, and the stamp is sent with this time at the next sign-in here (#621).
        if (e instanceof ApiRequestError && e.status < 500) throw e;
        const t = await accessToken().catch(() => undefined);
        if (t && config) {
          const cognito = cognitoApi(config);
          const device = readTokens()?.device?.key;
          if (device) await cognito.forgetDevice(t, device, 3_000).catch(() => undefined);
          await cognito.globalSignOut(t).catch(() => undefined);
        }
        if (me) rememberPendingSignOut(me.subject, at);
      }
      await signOut({ alreadyRecorded: true });
    },
  };
}

function SignOutEverywhere({ className = "", disabled = false }: { className?: string; disabled?: boolean }) {
  const everywhere = useSignOutEverywhere();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className={btnSecondary}
          disabled={busy || disabled}
          onClick={() => {
            setBusy(true);
            setError(undefined);
            void everywhere.run().catch((e: unknown) => {
              setError(securityError(e, "sign-out"));
              setBusy(false);
            });
          }}
        >
          {busy ? "Signing out…" : "Sign out everywhere"}
        </button>
        <span className="text-xs text-neutral-500">Signs out every browser and device you're signed in on, this one too, and forgets every remembered device.</span>
      </div>
      {error !== undefined && <ErrorState error={error} lead="Couldn’t sign out everywhere." />}
    </div>
  );
}

/** R11: the devices that skip the code at sign-in. This browser's is named; removing it keeps this session. */
function Devices({ api, token }: { api: Api; token: Token }) {
  const { ensureFresh } = useStepUp();
  const list = useCognitoList(
    useCallback(async () => {
      const all: DeviceRecord[] = [];
      let page: string | undefined;
      do {
        const r = await call(token, (t) => api.listDevices(t, page));
        all.push(...(r.Devices ?? []));
        page = r.PaginationToken;
      } while (page);
      return all;
    }, [api, token]),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const thisDevice = readTokens()?.device?.key;
  const nameOf = (d: DeviceRecord) => d.DeviceAttributes?.find((a) => a.Name === "device_name")?.Value ?? "A device";

  const remove = async (d: DeviceRecord) => {
    setBusy(true);
    setError(undefined);
    try {
      await ensureFresh();
      // Forgetting the device this session renews with would end the session (ForgetDevice kills its refresh token):
      // this browser's is un-remembered instead, and stays confirmed.
      if (d.DeviceKey === thisDevice) {
        await call(token, (t) => api.updateDeviceStatus(t, d.DeviceKey, "not_remembered"));
        unrememberSessionDevice(d.DeviceKey);
      } else {
        await call(token, (t) => api.forgetDevice(t, d.DeviceKey));
      }
      // The browser's own record of a remembered device too, or its next sign-in offers a device Cognito no longer remembers.
      removeRememberedDevice({ key: d.DeviceKey });
      await list.reload();
    } catch (e) {
      setError(securityError(e, "device"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Remembered devices">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        A device you chose to remember at sign-in skips the code from your authenticator app. Remove one you no longer use; Sign out everywhere forgets them all.
      </p>
      {list.loading && <LoadingState label="Loading remembered devices" />}
      {list.error !== undefined && <ErrorState error={securityError(list.error, "device")} onRetry={() => void list.reload()} />}
      {list.data && (
        <div className="mt-3">
          <DataTable
            caption="Your remembered devices"
            rows={list.data}
            rowKey={(d) => d.DeviceKey}
            filter={false}
            empty={<EmptyState title="No remembered devices" hint="Tick Remember this device when you sign in to skip the code on that device next time." />}
            columns={[
              { key: "name", header: "Device", render: (d) => (d.DeviceKey === thisDevice ? `${nameOf(d)} (this browser)` : nameOf(d)) },
              { key: "last", header: "Last used", render: (d) => (d.DeviceLastAuthenticatedDate ? <RelativeTime iso={new Date(d.DeviceLastAuthenticatedDate * 1000).toISOString()} /> : "—") },
              { key: "act", header: "", render: (d) => <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => void remove(d)} aria-label={`Remove ${nameOf(d)}`}>Remove</button> },
            ]}
          />
        </div>
      )}
      {error !== undefined && <ErrorState error={error} lead="Couldn’t remove that device." />}
    </Card>
  );
}

/** 13.3 [17]. Sign-ins from Cognito's history, and the user's own changes from the audit log, in their time zone. */
function SecurityActivity() {
  const api = useApi();
  const fmt = useFormat();
  const [data, setData] = useState<SecurityActivityResponse>();
  const [error, setError] = useState<unknown>();
  const [reported, setReported] = useState<Set<string>>(new Set());
  const load = useCallback(() => api<SecurityActivityResponse>("/v1/me/security-activity").then(setData).catch(setError), [api]);
  useEffect(() => {
    void load();
  }, [load]);
  const report = async (eventId: string) => {
    await api<OkResponse>("/v1/me/security-report", { method: "POST", body: JSON.stringify({ eventId }) });
    setReported((s) => new Set(s).add(eventId));
  };

  return (
    <Card title="Security activity">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">The last 90 days. If a sign-in is not yours, report it - Gairm is alerted at once.</p>
      {!data && error === undefined && <LoadingState label="Loading your activity" />}
      {error !== undefined && <ErrorState error={error} onRetry={() => void load()} />}
      {data && (
        <>
          {!data.signInsAvailable && <p className="mt-2 text-sm text-neutral-500">Sign-in history is not available on this deployment.</p>}
          <div className="mt-3">
            <DataTable
              caption="Sign-ins"
              rows={data.signIns}
              rowKey={(e) => e.id}
              empty={data.signInsAvailable ? <EmptyState title="No sign-ins recorded yet" /> : null}
              columns={[
                { key: "when", header: "When", render: (e) => <span title={e.at}>{fmt.when(e.at)}</span> },
                { key: "type", header: "Event", render: (e) => e.type },
                { key: "outcome", header: "Outcome", render: (e) => <StatusPill status={e.outcome.replace("_", " ")} tone={e.outcome === "pass" ? "ok" : e.outcome === "fail" ? "critical" : "neutral"} /> },
                { key: "where", header: "From", render: (e) => [e.ip, e.place].filter(Boolean).join(" · ") || "—" },
                { key: "device", header: "Device", render: (e) => e.device ?? "—" },
                {
                  key: "report",
                  header: "",
                  render: (e) =>
                    reported.has(e.id) ? (
                      <span className="text-xs text-green-700 dark:text-green-300">Reported</span>
                    ) : (
                      <button type="button" className="text-xs text-red-700 underline dark:text-red-300" onClick={() => void report(e.id)}>
                        Not me
                      </button>
                    ),
                },
              ]}
            />
          </div>
          <div className="mt-4">
            <DataTable
              caption="Changes to your account"
              rows={data.changes}
              rowKey={(c) => c.id}
              empty={<EmptyState title="No changes to your account in the last 90 days" />}
              columns={[
                { key: "when", header: "When", render: (c) => <span title={c.at}>{fmt.when(c.at)}</span> },
                { key: "what", header: "Change", render: (c) => <span title={c.action}>{actionLabel(c.action)}</span> },
                { key: "by", header: "By", render: (c) => (c.viaGairm ? `${c.actor} (Gairm staff)` : c.actor) },
                { key: "result", header: "Result", render: (c) => <StatusPill status={c.result} tone={resultTone(c.result)} /> },
              ]}
            />
          </div>
        </>
      )}
    </Card>
  );
}
